import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DefectRef } from '../../models/defect-ref.model';

export type AmpelState = 'nicht-freigabefaehig' | 'noch-offen' | 'freigabefaehig';

export interface AmpelCounts {
  failed: number;
  blocked: number;
  offen: number;
  gesamt: number;
}

/**
 * Freigabe-Ampel (page-release-freigabe.jsx:65-70): rot bei Fehlern oder Blockern, gelb bei
 * offenen Ausführungen, sonst grün. Abweichend vom Bundle ist ein Release ohne Testfall
 * „Noch offen" und nicht freigabefähig — es hat nichts bestanden (Klärung NEX-1452).
 * Ebenfalls abweichend macht ein offener interner Fehler die Ampel rot, auch wenn alle Fälle
 * bestanden sind — wie die Ampel des Abschlussberichts (Designänderung NEX-1778).
 */
export function ampelFor(x: AmpelCounts, offeneFehler = 0): AmpelState {
  if (x.failed > 0 || x.blocked > 0 || offeneFehler > 0) return 'nicht-freigabefaehig';
  if (x.offen > 0 || x.gesamt === 0) return 'noch-offen';
  return 'freigabefaehig';
}

/** Zahl der Einträge mit mindestens einem offenen internen Fehler (weder verifiziert noch abgelehnt). */
export function countOpenDefects(items: { defects: DefectRef[] }[]): number {
  return items.filter((item) => item.defects.some((d) => d.blocking)).length;
}

@Component({
  selector: 'app-ampel-badge',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ampel-badge" [attr.data-state]="state()" [class.ampel-badge--sm]="size() === 'sm'">
      <span class="ampel-badge__dot" aria-hidden="true"></span>
      {{ 'ampel.' + state() | translate }}
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .ampel-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.3125rem 0.75rem;
      border-radius: 999px;
      font-size: 0.78125rem;
      font-weight: 700;
      white-space: nowrap;

      &[data-state='nicht-freigabefaehig'] {
        background: var(--to-red-bg);
        color: var(--to-red-fg);
      }
      &[data-state='noch-offen'] {
        background: var(--to-warn-bg);
        color: var(--to-warn-fg);
      }
      &[data-state='freigabefaehig'] {
        background: var(--to-green-bg);
        color: var(--to-green-fg);
      }

      &--sm {
        padding: 0.1875rem 0.625rem;
        font-size: 0.71875rem;
      }
    }

    .ampel-badge__dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 999px;
      background: currentColor;
    }
  `,
})
export class AmpelBadgeComponent {
  readonly state = input.required<AmpelState>();
  readonly size = input<'md' | 'sm'>('md');
}
